<template>
  <div class="backpackCenter">
    <router-view></router-view>
    <section class="userBackpack">
      <h1>{{ t('user-backpacks.key001') }}</h1>
      <h2 class="backpack-alert">{{ t('user-backpacks.key002') }}</h2>
      <div class="filter-bar">
        <button v-for="category in backpackCategories" :key="category.id" :class="{ active: selectedCategory === category.id }" @click="selectCategory(category.id)">
          {{ category.title }}
        </button>
      </div>
      <main class="product-content">
        <section class="product-list-section" v-if="chunkedGiftItem[currentPage - 1]">
          <div class="product-list">
            <div
              class="product-card"
              v-for="(product, index) in chunkedGiftItem[currentPage - 1]"
              :key="'product-' + index"
              :ref="'product-' + index"
              @click="viewDetails(index)"
              :class="{ active: selectItemIndex === index || oldItemIndex === index }"
              v-show="product.quantity > 0"
            >
              <div class="product-header">
                <div class="profuct-img-box">
                  <img :src="product.image" :alt="product.title" class="product-image" @load="onImageLoad" />
                </div>
                <h3 class="product-title">{{ product.title }}</h3>
                <p class="quantity" v-if="product.id.includes('IPR')">{{ t('streamingVideo.key007') }} {{ product.quantity }}</p>
              </div>
            </div>
          </div>
          <Pagination
            :currentPage="currentPage"
            :totalPages="totalPages"
            :visiblePages="visiblePages"
            :hasMoreItems="hasMoreGiftItem"
            @goToPage="goToPage"
            @updatePage="updatePage"
            v-show="totalPages > 0"
          />
        </section>
        <section class="empty" v-else>
          <h2>{{ t('user-backpacks.key003') }}</h2>
        </section>
        <aside class="product-details" v-if="selectedItem && !isMobile">
          <div class="product-header">
            <h3 class="product-details-title">{{ selectedItem.title }}</h3>
            <div class="product-details-content">
              <RichContentRenderer :blocks="selectedItem.description"></RichContentRenderer>
            </div>
          </div>
        </aside>
        <aside class="product-details product-details-empty" v-else-if="!isMobile">
          <i class="bi bi-box"></i>
          <p>{{ $t('gift-shop.key005') }}</p>
        </aside>
      </main>
    </section>
  </div>
  <GiftItemMobileDetail
    v-if="selectedItem && isMobile"
    :userBackpack="false"
    :giftItemDetail="chunkedGiftItem[currentPage - 1]"
    :selectGiftItemIndex="selectItemIndex"
    :oldGiftItemIndex="oldItemIndex"
    :currentPage="currentPage"
    :totalPages="totalPages"
    @updatePage="updatePage"
    :pageSrc="userBackpack"
    @close="closeModal"
  />
  <Loading v-show="loading"></Loading>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { chunk } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getUserBackpack } from '@/api/apiService.js';
import Loading from '@/components/Loading.vue';
import Pagination from '@/components/Pagination.vue';
import GiftItemMobileDetail from '@/components/giftShop/giftItemMobileDetail.vue';
import RichContentRenderer from '@/components/richContent/RichContentRenderer.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const cloudfrontBaseUrl = process.env.VUE_APP_CLOUD_FRONT;
const lastKeyId = ref(undefined);
const isMobile = computed(() => store.state.isMobile);
const loading = ref(true);

const currentPage = ref(1);
const maxVisiblePages = 5;
const mobileItemsPerPage = 10;
const desktopItemsPerPage = 50;

const backpackData = ref({});
const selectedCategory = ref('all');
const oldItemIndex = ref(null);
const selectItemIndex = ref(null);

const cardRefs = ref([]);

// API Get UserBackpack Data
const userBackpack = async () => {
  try {
    const langSetting = route.params.lang === 'zh_tw' ? 'zh-tw' : route.params.lang;
    const jwt = store.state.jwt;
    const result = await getUserBackpack(langSetting, jwt);
    backpackData.value = result.data.data;
  } catch (error) {
    console.error(error);
  } finally {
    loading.value = false;
  }
};

//篩選 userBackpackVisible 參數（移除 ticket 顯示）
const visibleBackpackData = computed(() => Object.fromEntries(Object.entries(backpackData.value).filter(([, v]) => v.userBackpackVisible === true)));

//加上類別 id
const backpackCategories = computed(() => {
  const categories = Object.entries(visibleBackpackData.value).map(([key, value], index) => {
    // Add image src URL to each item
    const itemsWithImage = (value.list || []).map((item) => ({
      ...item,
      image: `${cloudfrontBaseUrl}/Image/Public/${key}/${item.id}.png`,
    }));

    return {
      id: `category_${index + 1}`,
      key,
      title: t(`user-backpacks.categories_${key}`),
      items: itemsWithImage,
    };
  });

  const allItems = categories.flatMap((cat) => cat.items);

  // Create the "ALL" category
  const allCategory = {
    id: 'all',
    key: 'all',
    title: t('gift-shop.key009'),
    items: allItems,
  };

  return [allCategory, ...categories];
});

const itemsPerPage = computed(() => (isMobile.value ? mobileItemsPerPage : desktopItemsPerPage));
const chunkedGiftItem = computed(() => {
  const category = backpackCategories.value.find((item) => item.id === selectedCategory.value);
  const filteredItems = category ? category.items : [];
  return chunk(filteredItems, itemsPerPage.value);
});
const totalPages = computed(() => chunkedGiftItem.value.length);
const hasMoreGiftItem = computed(() => lastKeyId.value != null);
const visiblePages = computed(() => {
  const total = totalPages.value;
  if (!total) return [];

  let startPage, endPage;
  const current = currentPage.value;

  if (total <= maxVisiblePages) {
    startPage = 1;
    endPage = total;
  } else {
    const middle = Math.floor(maxVisiblePages / 2);

    if (current <= middle) {
      startPage = 1;
      endPage = maxVisiblePages;
    } else if (current + middle >= total) {
      startPage = total - maxVisiblePages + 1;
      endPage = total;
    } else {
      startPage = current - middle;
      endPage = current + middle;
    }
  }

  return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
});

const selectedItem = computed(() => {
  const currentChunk = chunkedGiftItem.value[currentPage.value - 1];
  if (currentChunk && selectItemIndex.value !== null) {
    return currentChunk[selectItemIndex.value];
  }
  return null;
});

function viewDetails(index) {
  selectItemIndex.value = index;
}
function goToPage(page) {
  if (page > 0 && page <= totalPages.value) {
    currentPage.value = page;
  }
}
function updatePage(page) {
  currentPage.value = page;
}

function closeModal(oldIndex, productId) {
  oldItemIndex.value = oldIndex;
  selectItemIndex.value = null;
  document.body.style.overflow = '';

  const card = cardRefs.value[productId];
  if (card) {
    const offsetTop = card.offsetTop - 60;
    window.scrollTo({ top: offsetTop, behavior: 'smooth' });
  }
}

const selectCategory = (categoryId) => {
  selectedCategory.value = categoryId;
  currentPage.value = 1;
};

onMounted(() => {
  userBackpack();
});
</script>

<style lang="scss">
@import '@/assets/styles/pages/giftShop/giftShop.scss';
.backpackCenter {
  width: 80%;
  .backpack-alert {
    font-size: 14px;
    text-align: center;
    background: #2c2c2c;
    padding: 4px 8px;
    margin-bottom: 10px;
  }
  section .empty {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
.backpackCenter .userBackpack section.product-list-section {
  margin-top: 0;
  .product-header {
    padding-bottom: 10px;
  }
  .quantity {
    font-size: 14px;
    color: #fff599;
  }
}
.backpackCenter section.userBackpack h1 {
  font-size: 1.5em;
  padding: 5px 0;
  margin-bottom: 10px;
}
</style>
